/**
 * Review queue (design-reference §4.5). A server-sorted, server-filtered list of
 * open review items with bulk-select. Empty queue → the celebratory "All caught
 * up" state; a non-empty queue → the table plus a bulk-action bar. Every number on
 * screen comes from a real `GET /v1/review` field; the `NEW` chip is derived from
 * the effective taxonomy (`GET /v1/categories`). Bulk actions call
 * `POST /v1/review/bulk-resolve` and surface per-item conflicts from its response.
 */
import { useCallback, useId, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { ReviewQueueItem } from "../../api/schema";
import { useSession } from "../../auth/session";
import {
  Button,
  Checkbox,
  Combobox,
  ConfidenceMeter,
  ErrorState,
  Select,
  Skeleton,
  useToast,
  type ComboboxOption,
} from "../../components";

import "./review.css";
import {
  ageLabel,
  currentClassName,
  isProposedNew,
  reasonLabel,
  reasonTone,
  DEFAULT_SORT,
  REASON_FILTERS,
  SORT_OPTIONS,
} from "./format";
import { useReviewApi } from "./reviewApi";
import {
  REVIEW_PAGE_SIZE,
  REVIEW_PAGE_SIZES,
  useReviewQueue,
} from "./useReviewQueue";

/** Broadcast so the shell can refresh its Review nav badge after a mutation. */
export const REVIEW_CHANGED_EVENT = "docsight:review-changed";
function announceReviewChanged(): void {
  window.dispatchEvent(new CustomEvent(REVIEW_CHANGED_EVENT));
}

function ReasonBadge({
  reason,
}: {
  reason: ReviewQueueItem["reason"];
}): React.JSX.Element {
  return (
    <span className={`ds-reason ds-reason--${reasonTone(reason)}`}>
      {reasonLabel(reason)}
    </span>
  );
}

function docLabel(item: ReviewQueueItem): string {
  return item.document?.filename ?? item.document_id;
}

export function ReviewQueueScreen(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useSession();
  const api = useReviewApi();

  const sort = searchParams.get("sort") ?? DEFAULT_SORT;
  const reason = searchParams.get("reason") ?? "all";
  const rawPage = Number(searchParams.get("page"));
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 0;
  const rawPerPage = Number(searchParams.get("per"));
  const perPage = REVIEW_PAGE_SIZES.includes(rawPerPage)
    ? rawPerPage
    : REVIEW_PAGE_SIZE;
  const perPageId = useId();

  const state = useReviewQueue({ sort, reason, page, perPage });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const setParam = useCallback(
    (key: string, value: string, fallback: string) => {
      const next = new URLSearchParams(searchParams);
      if (value === fallback) next.delete(key);
      else next.set(key, value);
      // Back to page one: narrowing the reason filter or enlarging the page can
      // leave the current offset past the end of the result set, and the reader
      // would land on an empty queue having just asked to see more of it.
      next.delete("page");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (next: number) => {
      const params = new URLSearchParams(searchParams);
      if (next <= 0) params.delete("page");
      else params.set("page", String(next));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const go = useCallback(
    (to: string) => {
      void navigate(to);
    },
    [navigate],
  );

  const ready = state.status === "ready" ? state.data : null;
  const items = useMemo(() => ready?.items ?? [], [ready]);
  const categoryNames = ready?.categoryNames ?? [];

  const categoryOptions = useMemo<ComboboxOption[]>(
    () => ready?.categories.map((c) => ({ value: c.id, label: c.name })) ?? [],
    [ready],
  );

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected =
    items.length > 0 && items.every((it) => selected.has(it.id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size >= items.length ? new Set() : new Set(items.map((it) => it.id)),
    );
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const runBulk = useCallback(
    async (action: "assign_existing" | "dismiss", categoryId?: string) => {
      if (state.status !== "ready" || selected.size === 0) return;
      setBusy(true);
      try {
        const res = await api.bulkResolve({
          review_item_ids: [...selected],
          action,
          category_id: categoryId,
        });
        const resolved = res.resolved.length;
        const conflicts = res.conflicts.length;
        const verb =
          action === "assign_existing"
            ? `assigned "${categoryOptions.find((o) => o.value === categoryId)?.label ?? "category"}"`
            : "left as Other";
        const base = `${String(resolved)} item${resolved === 1 ? "" : "s"} ${verb}`;
        if (conflicts > 0) {
          toast(
            `${base} · ${String(conflicts)} already resolved by someone else`,
            { tone: "error" },
          );
        } else {
          toast(base);
        }
        clearSelection();
        announceReviewChanged();
        state.reload();
      } catch {
        toast("The bulk action could not complete — please try again.", {
          tone: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [api, selected, state, toast, categoryOptions, clearSelection],
  );

  // --- loading ---------------------------------------------------------------
  if (state.status === "loading") {
    return (
      <div className="ds-review" aria-busy="true">
        <div className="ds-review__head">
          <Skeleton width={220} height={16} />
        </div>
        <div className="ds-card">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="ds-review__skelrow">
              <Skeleton height={14} />
            </div>
          ))}
        </div>
        <p className="ds-review__loading">Loading the review queue…</p>
      </div>
    );
  }

  // --- error -----------------------------------------------------------------
  if (state.status === "error") {
    return (
      <div className="ds-review">
        <ErrorState
          title="We couldn't load the review queue"
          body="The review service didn't respond. Your documents are safe — this only affects the queue view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="primary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  // --- cleared (celebratory) -------------------------------------------------
  // Only an unfiltered empty queue is "caught up". A reason filter with no hits
  // keeps the toolbar (so the filter can be changed back) and says so in the table.
  if (state.data.total === 0 && reason === "all") {
    return (
      <div className="ds-review">
        <div className="ds-cleared" role="status">
          <div className="ds-cleared__tick" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="34" height="34" fill="none">
              <polyline
                points="3.5 8.5, 6.5 11.5, 12.5 4.5"
                stroke="var(--ok)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="ds-cleared__title">All caught up</h2>
          <p className="ds-cleared__body">
            The review queue is clear. Every correction you made is audited and
            feeds back into classification — the system just got a little
            sharper.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              go("/dashboard");
            }}
          >
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  // --- populated -------------------------------------------------------------
  return (
    <div className="ds-review ds-review--locked">
      <div className="ds-review__head">
        <p className="ds-review__count">
          <strong>
            {state.data.total} open item{state.data.total === 1 ? "" : "s"}
          </strong>{" "}
          · sorted{" "}
          {SORT_OPTIONS.find((o) => o.value === sort)?.label.toLowerCase() ??
            "newest first"}
        </p>
        <div className="ds-review__controls">
          <span className="ds-review__perpage">
            <label htmlFor={perPageId}>Rows</label>
            <Select
              id={perPageId}
              options={REVIEW_PAGE_SIZES.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              value={String(perPage)}
              onChange={(e) => {
                setParam(
                  "per",
                  e.currentTarget.value,
                  String(REVIEW_PAGE_SIZE),
                );
              }}
            />
          </span>
          <Select
            aria-label="Filter by reason"
            options={REASON_FILTERS}
            value={reason}
            onChange={(e) => {
              setParam("reason", e.currentTarget.value, "all");
            }}
          />
          <Select
            aria-label="Sort order"
            options={SORT_OPTIONS}
            value={sort}
            onChange={(e) => {
              setParam("sort", e.currentTarget.value, DEFAULT_SORT);
            }}
          />
        </div>
        <p className="ds-review__kbd">
          <span aria-hidden="true">
            keyboard-first: <kbd>↑↓</kbd> navigate <kbd>1</kbd> accept{" "}
            <kbd>2</kbd> new <kbd>3</kbd> other
          </span>
          <span className="sr-only">
            Keyboard shortcuts: up and down arrows to move between items, 1 to
            accept, 2 to create a new category, 3 to mark other.
          </span>
        </p>
      </div>

      {selected.size > 0 ? (
        <div className="ds-bulkbar" role="region" aria-label="Bulk actions">
          <span className="ds-bulkbar__count">{selected.size} selected</span>
          {hasPermission("review:resolve") ? (
            <>
              <div className="ds-bulkbar__assign">
                <Combobox
                  label="Assign selected items to a category"
                  hideLabel
                  options={categoryOptions}
                  value={null}
                  placeholder="Assign category…"
                  emptyText="No matching category"
                  onChange={(categoryId) => {
                    void runBulk("assign_existing", categoryId);
                  }}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  void runBulk("dismiss");
                }}
              >
                Leave as Other
              </Button>
            </>
          ) : null}
          <button
            type="button"
            className="ds-bulkbar__clear"
            onClick={clearSelection}
          >
            Clear
          </button>
        </div>
      ) : null}

      <div className="ds-card ds-review__tablewrap">
        <div className="ds-review__scroll">
          <table className="ds-qtable">
            <caption className="sr-only">Open review items</caption>
            <thead>
              <tr>
                <th scope="col" className="ds-qtable__check">
                  <Checkbox
                    aria-label={
                      allSelected ? "Deselect all items" : "Select all items"
                    }
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">Document</th>
                <th scope="col">Reason</th>
                <th scope="col">Suggested class</th>
                <th scope="col">Confidence</th>
                <th scope="col">Age</th>
                <th scope="col">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ds-qtable__empty">
                    No open items match this reason.{" "}
                    <button
                      type="button"
                      className="ds-linkbtn"
                      onClick={() => {
                        setParam("reason", "all", "all");
                      }}
                    >
                      Show all reasons
                    </button>
                  </td>
                </tr>
              ) : null}
              {items.map((item) => {
                const isNew = isProposedNew(
                  item.suggested_class,
                  categoryNames,
                );
                const currentClass = currentClassName(item);
                return (
                  <tr key={item.id} className="ds-qtable__row">
                    <td className="ds-qtable__check">
                      <Checkbox
                        aria-label={`Select ${docLabel(item)}`}
                        checked={selected.has(item.id)}
                        onChange={() => {
                          toggleRow(item.id);
                        }}
                      />
                    </td>
                    <td className="ds-qtable__doc" title={docLabel(item)}>
                      {docLabel(item)}
                    </td>
                    <td>
                      <ReasonBadge reason={item.reason} />
                    </td>
                    <td className="ds-qtable__suggested">
                      {item.suggested_class != null &&
                      item.suggested_class !== "" ? (
                        <>
                          <span>{item.suggested_class}</span>
                          {isNew ? (
                            <span className="ds-newchip">NEW</span>
                          ) : null}
                        </>
                      ) : currentClass != null ? (
                        // Not a suggestion: the class this document already
                        // carries (a low-confidence document keeps the real
                        // category the model named). Muted and chip-less so it
                        // never reads as something to accept.
                        <span
                          className="ds-qtable__current"
                          title="Current class — awaiting review"
                        >
                          {currentClass}
                        </span>
                      ) : (
                        <span className="ds-qtable__none">— no suggestion</span>
                      )}
                    </td>
                    <td>
                      <ConfidenceMeter
                        value={item.confidence}
                        size={5}
                        showThreshold={false}
                        label={`confidence for ${docLabel(item)}`}
                      />
                    </td>
                    <td className="ds-qtable__age">
                      {ageLabel(item.created_at)}
                    </td>
                    <td className="ds-qtable__action">
                      <button
                        type="button"
                        className="ds-linkbtn"
                        onClick={() => {
                          go(`/review/${item.id}`);
                        }}
                      >
                        Review →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ReviewFooter
          total={state.data.total}
          skip={state.data.skip}
          shown={items.length}
          perPage={perPage}
          onPrev={() => {
            setPage(page - 1);
          }}
          onNext={() => {
            setPage(page + 1);
          }}
        />
      </div>
    </div>
  );
}

function ReviewFooter({
  total,
  skip,
  shown,
  perPage,
  onPrev,
  onNext,
}: {
  total: number;
  skip: number;
  shown: number;
  perPage: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + shown, total);
  return (
    <div className="ds-review__footer">
      <span>
        Showing{" "}
        <strong style={{ color: "var(--t2)" }}>
          {from.toLocaleString("en-GB")}–{to.toLocaleString("en-GB")}
        </strong>{" "}
        of {total.toLocaleString("en-GB")} item{total === 1 ? "" : "s"}
      </span>
      <div className="ds-review__pager">
        <button
          type="button"
          className="ds-docs__pagebtn"
          onClick={onPrev}
          disabled={skip === 0}
        >
          ← Prev
        </button>
        <button
          type="button"
          className="ds-docs__pagebtn"
          onClick={onNext}
          disabled={skip + perPage >= total}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
